// src/utils/cacheRefreshPolicy.js
//
// The cache-version contract, rewritten 2026-09-05 (docs/bugs/firestore_cache_recovery_banner_loop_BUG.md).
//
// Two facts, kept apart on purpose:
//   1. "I have SEEN cache version N"  -> localStorage `gse_cache_version` (cacheVersionCheck.js).
//      Advanced the moment a tab learns about N. Never again tied to a successful delete: that
//      coupling was the banner loop (every boot saw local < remote until a single-tab delete
//      succeeded, which users could not and did not make happen).
//   2. "The shared IndexedDB has not been rebuilt since bump N" -> localStorage
//      `gse_cache_reset_pending` (this file). Cleared by the ONE code path that deletes the
//      database (clearFirestoreCache.js coordinator) after the delete actually succeeds.
//
// The rest of this file is the pure policy behind the background refresher
// (src/services/backgroundRefresh.js): given what a tab knows about itself and its peers, what
// should it do right now? Every input is a plain value so scripts/verify-firestore-cache-recovery.js
// can table-test it without a browser.
//
// Vocabulary
//   stale      this tab booted before the newest cache version it has heard of (old bundle, or
//              old data shapes in memory). A refresh (full reload) fixes it.
//   pending    the shared reset marker: the persistent cache still holds pre-bump documents.
//   park       reload this tab into memory-only Firestore so it releases the shared database.
//              A parked tab returns to persistence on its own once the reset is done.
//   reset      drain, terminate, delete, reload: the C-09 single-deletion path, unchanged, now run
//              by whichever tab proves it is alone instead of by a user who closed other tabs.
//   quiet      hidden, or visible but idle for the threshold; and no blocker (a live call, a
//              walkie call, a record under edit, an upload) says "not now".

export const CACHE_RESET_PENDING_KEY = 'gse_cache_reset_pending'
export const PARKED_REASON_CODE = 'cache-reset-parked'
export const REFRESH_IDLE_MS = 5 * 60 * 1000
export const REFRESH_IDLE_FORCE_RELOAD_MS = 60 * 1000
export const REFRESH_TICK_MS = 15 * 1000
export const REFRESH_JITTER_MAX_MS = 30 * 1000
// Memory-mode reasons that mean "something failed"; those tabs stay memory-only for their
// lifetime (a fresh tab retries persistence) so a wedged IndexedDB cannot become a reload loop.
export const RECOVERY_FAILURE_REASON_CODES = Object.freeze([
  'auth-bootstrap-timeout',
  'indexeddb-corruption',
  'cache-reset-error',
  'cache-reset-timeout',
])

function safeJsonParse(value) {
  try {
    return JSON.parse(value)
  } catch {
    return null
  }
}

function browserStorage(name) {
  try {
    return typeof window !== 'undefined' ? window[name] : null
  } catch {
    return null
  }
}

// ── pending-reset marker ────────────────────────────────────────────────────

export function readPendingReset(storage = browserStorage('localStorage')) {
  let raw = null
  try {
    raw = storage?.getItem(CACHE_RESET_PENDING_KEY) || null
  } catch {
    return null
  }
  if (!raw) return null
  const parsed = safeJsonParse(raw)
  const version = Number(parsed?.version)
  if (!Number.isSafeInteger(version) || version <= 0) {
    try {
      storage?.removeItem(CACHE_RESET_PENDING_KEY)
    } catch {
      // nothing to do: a malformed marker is treated as absent
    }
    return null
  }
  return {
    version,
    forceReload: parsed.forceReload === true,
    reason: typeof parsed.reason === 'string' ? parsed.reason : '',
    since: Number(parsed.since) || 0,
  }
}

/**
 * Record that the persistent cache needs rebuilding for `version`. Never lowers an existing
 * marker's version; keeps forceReload once it was set for that version or a newer bump.
 */
export function writePendingReset(
  storage = browserStorage('localStorage'),
  { version, forceReload = false, reason = '', now = Date.now() } = {}
) {
  const next = Number(version)
  if (!Number.isSafeInteger(next) || next <= 0) return false
  const current = readPendingReset(storage)
  if (current && current.version > next) return true
  const marker = {
    version: next,
    forceReload: forceReload === true || (current?.version === next && current.forceReload),
    reason: String(reason || current?.reason || ''),
    since: current?.version === next && current.since ? current.since : now,
  }
  try {
    storage?.setItem(CACHE_RESET_PENDING_KEY, JSON.stringify(marker))
    return storage?.getItem(CACHE_RESET_PENDING_KEY) !== null
  } catch {
    return false
  }
}

export function clearPendingReset(storage = browserStorage('localStorage')) {
  try {
    storage?.removeItem(CACHE_RESET_PENDING_KEY)
    return true
  } catch {
    return false
  }
}

export function isPendingResetFor(storage, version) {
  const pending = readPendingReset(storage)
  return !!pending && pending.version >= Number(version)
}

// ── boot-reset budget (loop insurance) ──────────────────────────────────────

export const BOOT_RESET_BUDGET_KEY = 'gse_cache_boot_resets'
export const BOOT_RESET_WINDOW_MS = 5 * 60 * 1000
export const BOOT_RESET_MAX = 2

/**
 * Record that this tab is about to run a boot-time rebuild and say whether it may. A rebuild that
 * "succeeds" without clearing the pending marker would otherwise reload forever; two attempts per
 * five minutes per tab is the ceiling, after which the tab boots normally and the background
 * refresher takes over. sessionStorage: the budget is per tab, like the loop it guards against.
 */
export function noteBootReset(
  storage = browserStorage('sessionStorage'),
  { now = Date.now(), windowMs = BOOT_RESET_WINDOW_MS, max = BOOT_RESET_MAX } = {}
) {
  let recent = []
  try {
    const parsed = safeJsonParse(storage?.getItem(BOOT_RESET_BUDGET_KEY))
    recent = Array.isArray(parsed)
      ? parsed.map(Number).filter(t => Number.isFinite(t) && now - t < windowMs)
      : []
  } catch {
    recent = []
  }
  const allowed = recent.length < max
  if (allowed) {
    try {
      storage?.setItem(BOOT_RESET_BUDGET_KEY, JSON.stringify([...recent, now]))
    } catch {
      // Without storage there is no budget to keep; allow the attempt.
    }
  }
  return allowed
}

// ── memory-mode classification ──────────────────────────────────────────────

export function isParkedFallback(fallback) {
  return fallback?.active === true && fallback.reasonCode === PARKED_REASON_CODE
}

export function isFailureFallback(fallback) {
  return fallback?.active === true && RECOVERY_FAILURE_REASON_CODES.includes(fallback.reasonCode)
}

// ── quiet / idle ────────────────────────────────────────────────────────────

export function idleThresholdFor(pending) {
  return pending?.forceReload ? REFRESH_IDLE_FORCE_RELOAD_MS : REFRESH_IDLE_MS
}

/**
 * A tab is quiet when nobody is looking at it (hidden) or nobody has touched it for the
 * threshold. Blockers are decided separately: a quiet tab on a live call still never refreshes.
 */
export function isQuiet({ visibilityState, idleMs, idleThresholdMs = REFRESH_IDLE_MS } = {}) {
  if (visibilityState === 'hidden') return true
  return Number.isFinite(idleMs) && idleMs >= idleThresholdMs
}

export function jitterMs(random = Math.random, maxMs = REFRESH_JITTER_MAX_MS) {
  const r = Number(random())
  const unit = Number.isFinite(r) ? Math.min(Math.max(r, 0), 1) : 0
  return Math.floor(unit * maxMs)
}

// ── the decision ────────────────────────────────────────────────────────────

/**
 * What should this tab do right now?
 *
 * @param {object} state
 *   enabled          feature flag (frontendFlags.backgroundRefresh_enabled)
 *   blocked          any refresh blocker is active (live call, walkie, record under edit, upload)
 *   quiet            hidden or idle past the threshold
 *   navigating       the user is starting an in-app route change (a full navigation costs about
 *                    what the route change costs; visible tabs refresh here)
 *   stale            this tab booted before the newest version it has heard of
 *   pending          the shared pending-reset marker, or null
 *   mode             'persistent' | 'memory'
 *   parked           memory mode entered by the refresher (returns on its own); false for the
 *                    failure reasons, which stay memory-only for the tab's lifetime
 *   liveClients      persistent Firestore clients after pruning stale entries (self included when
 *                    this tab is persistent)
 *   pendingMutations count of firestore_mutations_* markers (unsynced writes of any tab)
 * @returns {{action: 'none'|'reset'|'park'|'reload'|'navigate'|'restore', reason: string}}
 */
export function decideRefreshAction(state = {}) {
  const {
    enabled = true,
    blocked = false,
    quiet = false,
    navigating = false,
    stale = false,
    pending = null,
    mode = 'persistent',
    parked = false,
    liveClients = 0,
    pendingMutations = 0,
  } = state

  if (!enabled) return { action: 'none', reason: 'disabled' }
  if (blocked) return { action: 'none', reason: 'blocked' }
  if (!quiet && !navigating) return { action: 'none', reason: 'in-use' }

  const refresh = navigating ? 'navigate' : 'reload'

  if (mode === 'memory') {
    if (!parked) {
      // A failure-mode tab never returns to persistence by itself; it still takes new bundles.
      return stale
        ? { action: refresh, reason: 'stale-bundle' }
        : { action: 'none', reason: 'recovery-mode' }
    }
    if (!pending) return { action: 'restore', reason: 'reset-done' }
    if (liveClients === 0) {
      // Every persistent tab is closed or parked: nobody else will ever run the reset.
      if (pendingMutations === 0) return { action: 'reset', reason: 'no-persistent-clients' }
      // A dead tab left unsynced writes behind; only a persistent client can sync them, and
      // they block the delete until it does. Go back to persistence and let the SDK work.
      return { action: 'restore', reason: 'stranded-writes' }
    }
    return { action: 'none', reason: 'waiting-for-reset' }
  }

  if (pending) {
    if (liveClients <= 1) {
      if (pendingMutations === 0) return { action: 'reset', reason: 'alone' }
      // This tab is the only client and the queue is not empty: it is the one that will sync
      // those writes. Parking it would strand them (and the delete they block). Wait.
      return { action: 'none', reason: 'pending-writes' }
    }
    if (quiet) return { action: 'park', reason: 'peers-hold-database' }
    // navigating with peers: the user keeps the fast path; new bundle if this tab is stale
    return stale
      ? { action: refresh, reason: 'stale-bundle' }
      : { action: 'none', reason: 'peers-hold-database' }
  }

  return stale ? { action: refresh, reason: 'stale-bundle' } : { action: 'none', reason: 'current' }
}

// Composer drafts need no help from this module: MessageComposer mirrors them to localStorage
// per entity and channel (useComposerDrafts) and flushes the debounce on pagehide, so a reload
// from here restores them exactly as a tab switch would.
